/**
 * Frontend helper for sending transactional email via the send-email
 * Edge Function.
 *
 * Scope (intentional): this is a typed wrapper around supabase.functions
 * .invoke('send-email', ...). It does NOT do template rendering, retries,
 * or queueing — it's the same foundation level as the Edge Function itself.
 *
 * Usage:
 *   const { data, error } = await sendEmail({
 *     to: 'user@example.com',
 *     subject: 'Welcome',
 *     html: '<p>Hello!</p>',
 *   })
 *
 * Returns the same shape as supabase.functions.invoke for consistency
 * with the rest of the codebase: { data: { id } | null, error: Error | null }.
 *
 * The error branch contains a real Error instance with a useful message
 * — both gateway-level rejections (401 missing JWT, 405 wrong method)
 * and function-level rejections (400 missing field, 502 Resend error)
 * are surfaced uniformly.
 */

import { supabase } from './supabase'

export type SendEmailInput = {
  /** Recipient address. Pass an array for multiple recipients. */
  to: string | string[]
  subject: string
  /** HTML body. Plaintext-only sends are not supported in the foundation; wrap text in <p> tags. */
  html: string
  /** Optional reply-to address. If omitted, replies go to the sender. */
  replyTo?: string
  /** Optional file attachments (e.g. an .ics invite). content = base64.
   *  Additive — existing callers omit this and behave identically. The
   *  Edge Function caps count/size and forwards to Resend verbatim. */
  attachments?: Array<{ filename: string; content: string }>
}

export type SendEmailResult = {
  /** Resend's message ID for the sent email. Useful for support / debugging. */
  id: string
}

/**
 * Send a transactional email through the Edge Function.
 *
 * Always returns a {data, error} tuple — never throws. Callers should
 * check the error before using the data.
 */
export async function sendEmail(
  input: SendEmailInput,
): Promise<{ data: SendEmailResult | null; error: Error | null }> {
  const { data, error } = await supabase.functions.invoke<SendEmailResult>(
    'send-email',
    { body: input },
  )

  if (error) {
    // supabase.functions.invoke surfaces non-2xx as an error but doesn't
    // pull the response body by default. Try to read the body for a useful
    // message so callers (and toasts) get something better than "Edge
    // Function returned a non-2xx status code".
    const ctx = (error as any).context
    let message = error.message
    if (ctx && typeof ctx.text === 'function') {
      try {
        const text = await ctx.text()
        const parsed = JSON.parse(text)
        // Both shapes are possible:
        // - Our function: { error: "..." }
        // - Supabase gateway: { code: "...", message: "..." }
        message = parsed?.error || parsed?.message || message
      } catch {
        /* keep default */
      }
    }
    return { data: null, error: new Error(message) }
  }

  if (!data?.id) {
    return { data: null, error: new Error('No message ID returned') }
  }

  return { data, error: null }
}
